import React from "react";
import { motion } from "framer-motion";
import {
  Home,
  Trees,
  Scroll,
  Users,
  LandPlot,
  AlertTriangle,
  FileWarning,
  Hammer,
} from "lucide-react";

const items = [
  {
    icon: AlertTriangle,
    title: "Pre-Foreclosure & Behind on Payments",
    body: "Falling behind doesn’t have to end at auction. A fast cash sale can protect your equity and your credit.",
  },
  {
    icon: Home,
    title: "Houses in Any Condition",
    body: "Fire damage, foundation issues, decades of deferred repairs — we buy houses exactly as they sit. Leave behind anything you don’t want.",
  },
  {
    icon: Trees,
    title: "Vacant Land & Acreage",
    body: "Rural acreage, timberland, pasture — any size, any condition.",
  },
  {
    icon: Scroll,
    title: "Inherited & Probate Property",
    body: "Land you never asked for, handled with patience and care.",
  },
  {
    icon: Users,
    title: "Tired Landlords & Tenant-Occupied",
    body: "Done with midnight calls and turnover? We buy rentals with tenants in place.",
  },
  {
    icon: LandPlot,
    title: "Infill & Residential Lots",
    body: "Buildable lots in town, in subdivisions, or near new construction.",
  },
  {
    icon: FileWarning,
    title: "Back Taxes & Title Issues",
    body: "Liens, unpaid taxes, missing heirs, boundary questions — we work through them.",
  },
  {
    icon: Hammer,
    title: "Teardowns & Distressed Structures",
    body: "When the land is worth more than what’s sitting on it.",
  },
];

const WhatWeBuy = () => {
  return (
    <section
      id="what-we-buy"
      className="section-pad"
      style={{ background: "#F0EAE0" }}
      data-testid="what-we-buy-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="eyebrow">No Property Is Too Complicated</p>
          <h2
            className="font-heading font-medium mt-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              color: "#122B20",
              letterSpacing: "-0.015em",
            }}
          >
            Houses. Land. <span className="gold-italic">Everything In Between.</span>
          </h2>
          <p
            className="mt-5 font-body text-[#2B2926]/85"
            style={{ fontSize: "1.05rem", lineHeight: 1.7 }}
          >
            A house that needs work. A lot you’ve been paying taxes on for
            years. An inherited property three states away. Whatever you’re
            holding, selling it should be simple — here’s what we buy.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
        >
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.article
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="group relative p-7 md:p-8 rounded-2xl transition-all duration-300"
                style={{
                  background: "#FAF7F2",
                  border: "1px solid rgba(168,162,154,0.35)",
                }}
                data-testid={`what-we-buy-item-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-300"
                    style={{
                      background: "rgba(27,67,50,0.06)",
                      border: "1px solid rgba(27,67,50,0.15)",
                    }}
                  >
                    <Icon size={20} color="#1B4332" strokeWidth={1.5} />
                  </div>
                  <div
                    className="font-heading text-sm opacity-40 group-hover:opacity-100 transition-opacity"
                    style={{ color: "#C6A15B" }}
                  >
                    0{i + 1}
                  </div>
                </div>
                <h3
                  className="font-heading font-medium mt-6 text-xl md:text-[1.35rem]"
                  style={{ color: "#122B20", lineHeight: 1.25 }}
                >
                  {it.title}
                </h3>
                <p
                  className="mt-3 font-body text-[#2B2926]/80"
                  style={{ fontSize: "0.94rem", lineHeight: 1.65 }}
                >
                  {it.body}
                </p>
                <div
                  className="absolute inset-x-0 bottom-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: "rgba(198,161,91,0.6)" }}
                />
                <style>{`
                  [data-testid="what-we-buy-item-${i}"]:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 24px 40px -24px rgba(18,43,32,0.25);
                    border-color: rgba(198,161,91,0.4);
                  }
                `}</style>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeBuy;
